import React, { useMemo, useState } from "react";
import Papa from "papaparse";
import ModalBasic from "../../components/ModalBasic.jsx";
import FileUpload from "../../components/FileUpload.jsx";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons.jsx";
import { bulkCreateMarketplaceSellers } from "../../api/admin.js";

const REQUIRED_COLUMNS = [
  "name",
  "legal_name",
  "signer_name",
  "address",
  "city",
  "state",
  "zip",
  "email",
  "phone",
  "pos_system",
  "agreement_type",
  "agreement_template_id",
  "saucey_signer_name",
  "saucey_signer_email"
];

const OPTIONAL_COLUMNS = [
  "store_id",
  "organization",
  "general_commission",
  "non_tobacco_commission",
  "nicotine_commission",
  "merchant_subscription_fee",
  "platform_service_fee",
  "legal_signer_title",
  "legal_signer_name",
  "legal_signer_email",
];
const ALL_COLUMNS = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS];

const COMMISSION_COLUMNS = new Set([
  "general_commission",
  "non_tobacco_commission",
  "nicotine_commission",
]);

function cleanField(col, raw) {
  const v = String(raw).trim();
  if (COMMISSION_COLUMNS.has(col)) {
    return Number(v.replace(/%/g, "").trim());
  }
  return v;
}

function normalizeHeader(h) {
  return String(h || "")
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STATE_RE = /^[A-Za-z]{2}$/;
const ZIP_RE = /^\d{5}(-\d{4})?$/;
const URL_RE = /^https?:\/\/\S+$/i;

function validateField(col, value) {
  if (!value || !String(value).trim()) return "missing";
  const v = String(value).trim();
  switch (col) {
    case "email":
      return EMAIL_RE.test(v) ? null : "invalid email format";
    case "state":
      return STATE_RE.test(v) ? null : "state must be 2 letters";
    case "zip":
      return ZIP_RE.test(v) ? null : "zip must be 5 digits";
    case "phone": {
      const digits = v.replace(/\D/g, "");
      return digits.length === 10 || digits.length === 11
        ? null
        : "phone must be 10 digits";
    }
    case "general_commission":
    case "non_tobacco_commission":
    case "nicotine_commission": {
      const stripped = v.replace(/%/g, "").trim();
      const n = Number(stripped);
      if (!Number.isFinite(n)) return "must be a number";
      if (n < 0 || n > 100) return "must be between 0 and 100";
      return null;
    }
    default:
      return null;
  }
}

function validateRows(rawRows) {
  const annotated = rawRows.map((row, i) => {
    const issues = [];
    const missing = [];
    REQUIRED_COLUMNS.forEach((col) => {
      const issue = validateField(col, row[col]);
      if (issue === "missing") missing.push(col);
      else if (issue) issues.push(`${col}: ${issue}`);
    });
    OPTIONAL_COLUMNS.forEach((col) => {
      const raw = row[col];
      if (raw == null || String(raw).trim() === "") return;
      const issue = validateField(col, raw);
      if (issue && issue !== "missing") issues.push(`${col}: ${issue}`);
    });
    if (missing.length) issues.unshift(`missing ${missing.join(", ")}`);

    // Commission rule (mutually exclusive): either general_commission alone,
    // or both non_tobacco_commission AND nicotine_commission with general blank.
    const hasGeneral = String(row.general_commission || "").trim() !== "";
    const hasNonTobacco = String(row.non_tobacco_commission || "").trim() !== "";
    const hasNicotine = String(row.nicotine_commission || "").trim() !== "";
    if (hasGeneral && (hasNonTobacco || hasNicotine)) {
      issues.push(
        "general_commission cannot be combined with non_tobacco_commission or nicotine_commission"
      );
    } else if (!hasGeneral && !(hasNonTobacco && hasNicotine)) {
      issues.push(
        "provide general_commission, or both non_tobacco_commission and nicotine_commission"
      );
    }

    return { row, rowNum: i + 2, issues };
  });

  // Duplicate email detection — only consider rows whose email is at least well-formed
  const emailCounts = new Map();
  annotated.forEach(({ row }) => {
    const e = String(row.email || "").trim().toLowerCase();
    if (!e || !EMAIL_RE.test(e)) return;
    emailCounts.set(e, (emailCounts.get(e) || 0) + 1);
  });
  annotated.forEach((r) => {
    const e = String(r.row.email || "").trim().toLowerCase();
    if (e && emailCounts.get(e) > 1) {
      r.issues.push("duplicate email in file");
    }
  });

  const valid = [];
  const invalid = [];
  annotated.forEach(({ row, rowNum, issues }) => {
    if (issues.length) {
      invalid.push({ row: rowNum, reason: issues.join("; "), data: row });
    } else {
      const cleaned = {};
      REQUIRED_COLUMNS.forEach((col) => {
        cleaned[col] = cleanField(col, row[col]);
      });
      OPTIONAL_COLUMNS.forEach((col) => {
        const raw = row[col];
        if (raw == null || String(raw).trim() === "") return;
        cleaned[col] = cleanField(col, raw);
      });
      valid.push(cleaned);
    }
  });
  return { valid, invalid };
}

function downloadErrorsCSV(errors) {
  const rows = errors.map((e) => ({
    row: e.row,
    reason: e.reason,
    ...e.data,
  }));
  const csv = Papa.unparse(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `marketplace-upload-errors-${Date.now()}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const Stage = {
  Picking: "picking",
  Previewing: "previewing",
  Submitting: "submitting",
  Done: "done",
};

function MarketplaceCSVUpload({ modalOpen, setModalOpen, onComplete }) {
  const [stage, setStage] = useState(Stage.Picking);
  const [parseError, setParseError] = useState(null);
  const [valid, setValid] = useState([]);
  const [invalid, setInvalid] = useState([]);
  const [missingColumns, setMissingColumns] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStage(Stage.Picking);
    setParseError(null);
    setValid([]);
    setInvalid([]);
    setMissingColumns([]);
    setSubmitError(null);
    setResult(null);
  };

  const handleClose = () => {
    setModalOpen(false);
    setTimeout(reset, 250);
  };

  const handleFile = (files) => {
    if (!files || !files.length) return;
    const file = files[0];
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: normalizeHeader,
      complete: (results) => {
        const headers = results.meta?.fields ?? [];
        const missing = REQUIRED_COLUMNS.filter((col) => !headers.includes(col));
        if (missing.length) {
          setMissingColumns(missing);
          setStage(Stage.Previewing);
          setValid([]);
          setInvalid([]);
          return;
        }
        const { valid: v, invalid: iv } = validateRows(results.data);
        setMissingColumns([]);
        setValid(v);
        setInvalid(iv);
        setStage(Stage.Previewing);
      },
      error: (err) => {
        setParseError(err.message || "Failed to parse CSV");
      },
    });
  };

  const handleSubmit = async () => {
    setStage(Stage.Submitting);
    setSubmitError(null);
    try {
      const res = await bulkCreateMarketplaceSellers(valid);
      console.log(JSON.stringify(res));
      setResult({
        created: res.created || [],
        errors: res.errors || [],
      });
      setStage(Stage.Done);
      if (onComplete) onComplete();
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message || err?.message || "Upload failed"
      );
      setStage(Stage.Previewing);
    }
  };

  const totalRows = valid.length + invalid.length;
  const canSubmit = useMemo(
    () => stage === Stage.Previewing && valid.length > 0 && missingColumns.length === 0,
    [stage, valid.length, missingColumns.length]
  );

  return (
    <ModalBasic
      title="Bulk Upload Marketplace Sellers"
      modalOpen={modalOpen}
      setModalOpen={handleClose}
    >
      <div className="px-5 py-4 space-y-4">
        {stage === Stage.Picking && (
          <>
            <div className="text-sm text-slate-600 dark:text-slate-300">
              Upload a CSV with the following required columns:
              <div className="mt-2 flex flex-wrap gap-1">
                {REQUIRED_COLUMNS.map((c) => (
                  <code
                    key={c}
                    className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-xs"
                  >
                    {c}
                  </code>
                ))}
              </div>
              {OPTIONAL_COLUMNS.length > 0 && (
                <>
                  <div className="mt-3">Optional columns:</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {OPTIONAL_COLUMNS.map((c) => (
                      <code
                        key={c}
                        className="px-2 py-0.5 bg-slate-50 dark:bg-slate-800/40 border border-dashed border-slate-300 dark:border-slate-700 rounded text-xs"
                      >
                        {c}
                      </code>
                    ))}
                  </div>
                </>
              )}
              <p className="mt-3 text-xs text-slate-500">
                Commissions: each row must have either{" "}
                <code>general_commission</code> on its own, or both{" "}
                <code>non_tobacco_commission</code> and{" "}
                <code>nicotine_commission</code> — not both shapes at once.
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Store ID, city, and team are assigned by the backend.
              </p>
            </div>
            <FileUpload
              accept=".csv,text/csv"
              onChange={handleFile}
              inputContent="Drop CSV here or click to browse"
            />
            {parseError && (
              <div className="text-sm text-red-500">{parseError}</div>
            )}
          </>
        )}

        {stage === Stage.Previewing && (
          <>
            {missingColumns.length > 0 ? (
              <div className="rounded border border-red-300 bg-red-50 dark:bg-red-900/20 p-3 text-sm">
                <div className="font-semibold text-red-700 dark:text-red-300 mb-1">
                  CSV is missing required columns
                </div>
                <div className="flex flex-wrap gap-1">
                  {missingColumns.map((c) => (
                    <code
                      key={c}
                      className="px-2 py-0.5 bg-white dark:bg-slate-800 rounded text-xs"
                    >
                      {c}
                    </code>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded border border-slate-200 dark:border-slate-700 p-3">
                  <div className="text-xs uppercase text-slate-500">Total rows</div>
                  <div className="text-2xl font-semibold">{totalRows}</div>
                </div>
                <div className="rounded border border-emerald-200 dark:border-emerald-800 p-3">
                  <div className="text-xs uppercase text-emerald-600">Valid</div>
                  <div className="text-2xl font-semibold text-emerald-600">
                    {valid.length}
                  </div>
                </div>
                <div className="rounded border border-amber-200 dark:border-amber-800 p-3">
                  <div className="text-xs uppercase text-amber-600">Invalid</div>
                  <div className="text-2xl font-semibold text-amber-600">
                    {invalid.length}
                  </div>
                </div>
              </div>
            )}

            {invalid.length > 0 && (
              <details className="rounded border border-slate-200 dark:border-slate-700">
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                  Invalid rows ({invalid.length})
                </summary>
                <div className="max-h-48 overflow-y-auto px-3 py-2 text-xs space-y-1">
                  {invalid.slice(0, 50).map((e) => (
                    <div key={e.row} className="flex gap-2">
                      <span className="text-slate-400">Row {e.row}:</span>
                      <span className="text-amber-600">{e.reason}</span>
                    </div>
                  ))}
                  {invalid.length > 50 && (
                    <div className="text-slate-400 italic">
                      ...and {invalid.length - 50} more
                    </div>
                  )}
                </div>
              </details>
            )}

            {submitError && (
              <div className="text-sm text-red-500">{submitError}</div>
            )}

            <div className="flex justify-between items-center pt-2">
              <SecondaryButton onClick={reset}>Choose different file</SecondaryButton>
              <PrimaryButton onClick={handleSubmit} disabled={!canSubmit}>
                Create {valid.length} sellers
              </PrimaryButton>
            </div>
          </>
        )}

        {stage === Stage.Submitting && (
          <div className="py-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto mb-3"></div>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Creating {valid.length} sellers...
            </p>
          </div>
        )}

        {stage === Stage.Done && result && (
          <>
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="rounded border border-emerald-200 dark:border-emerald-800 p-3">
                <div className="text-xs uppercase text-emerald-600">Created</div>
                <div className="text-2xl font-semibold text-emerald-600">
                  {result.created.length}
                </div>
              </div>
              <div className="rounded border border-red-200 dark:border-red-800 p-3">
                <div className="text-xs uppercase text-red-600">Errors</div>
                <div className="text-2xl font-semibold text-red-600">
                  {result.errors.length}
                </div>
              </div>
            </div>

            {result.created.length > 0 && (
              <details
                open={result.errors.length === 0}
                className="rounded border border-emerald-200 dark:border-emerald-800"
              >
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                  Created ({result.created.length})
                </summary>
                <div className="max-h-48 overflow-y-auto px-3 py-2 text-xs space-y-1">
                  {result.created.slice(0, 50).map((c, idx) => (
                    <div key={c.store_id ?? idx} className="flex gap-2">
                      <span className="text-slate-400">
                        {c.store_id ?? `#${idx + 1}`}:
                      </span>
                      <span className="text-emerald-700 dark:text-emerald-300">
                        {c.name ?? "(no name)"}
                      </span>
                    </div>
                  ))}
                  {result.created.length > 50 && (
                    <div className="text-slate-400 italic">
                      ...and {result.created.length - 50} more
                    </div>
                  )}
                </div>
              </details>
            )}

            {result.errors.length > 0 && (
              <details open className="rounded border border-red-200 dark:border-red-800">
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                  Errors ({result.errors.length})
                </summary>
                <div className="max-h-48 overflow-y-auto px-3 py-2 text-xs space-y-1">
                  {result.errors.slice(0, 50).map((e, idx) => (
                    <div key={idx} className="flex gap-2">
                      <span className="text-slate-400">Row {e.row ?? idx + 1}:</span>
                      <span className="text-red-500">{e.reason}</span>
                    </div>
                  ))}
                  {result.errors.length > 50 && (
                    <div className="text-slate-400 italic">
                      ...and {result.errors.length - 50} more
                    </div>
                  )}
                </div>
              </details>
            )}

            <div className="flex justify-between items-center pt-2">
              {result.errors.length > 0 ? (
                <SecondaryButton onClick={() => downloadErrorsCSV(result.errors)}>
                  Download errors CSV
                </SecondaryButton>
              ) : (
                <span />
              )}
              <PrimaryButton onClick={handleClose}>Done</PrimaryButton>
            </div>
          </>
        )}
      </div>
    </ModalBasic>
  );
}

export default MarketplaceCSVUpload;
